import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import * as marketplaceAuthService from '../../services/marketplaceAuthService';
import { 
  Box, 
  Typography, 
  Paper, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemSecondaryAction,
  IconButton, 
  Divider, 
  Button, 
  Alert, 
  CircularProgress,
  Tooltip,
  Chip
} from '@mui/material';
import { 
  DeleteOutline as DeleteIcon, 
  DevicesOther as DeviceIcon,
  Refresh as RefreshIcon,
  AccessTime as TimeIcon,
  CheckCircle as ActiveIcon,
  Computer as ComputerIcon,
  PhoneAndroid as PhoneIcon,
  Tablet as TabletIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

// Styled components
const Container = styled(Box)`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(180deg, #030617 0%, #0a1128 100%);
  padding: 1rem;
`;

const StyledPaper = styled(Paper)`
  padding: 2rem;
  width: 100%;
  max-width: 800px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 10px;
  color: white;
`;

const Title = styled(Typography)`
  margin-bottom: 1rem;
  text-align: center;
  color: white;
`;

const Subtitle = styled(Typography)`
  margin-bottom: 2rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
`;

const StyledListItem = styled(ListItem)`
  border-radius: 8px;
  margin-bottom: 1rem;
  background: rgba(255, 255, 255, 0.03);
  transition: background-color 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
  
  &.current-session {
    border-left: 4px solid #4a90e2;
  }
`;

const DeviceInfo = styled(Box)`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const SessionTime = styled(Box)`
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8rem;
`;

const ActionButton = styled(Button)`
  margin-top: 1rem;
`;

const InfoBox = styled(Box)`
  background: rgba(74, 144, 226, 0.1);
  border-left: 4px solid #4a90e2;
  padding: 1rem;
  margin-bottom: 1.5rem;
  border-radius: 4px;
`;

const NoSessionsMessage = styled(Box)`
  text-align: center;
  padding: 2rem;
  color: rgba(255, 255, 255, 0.7);
`;

// Helper function to get device icon
const getDeviceIcon = (device) => {
  switch (device.toLowerCase()) {
    case 'mobile':
      return <PhoneIcon />;
    case 'tablet':
      return <TabletIcon />;
    default:
      return <ComputerIcon />;
  }
};

// Session Manager Component
const SessionManager = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [terminatingSession, setTerminatingSession] = useState(null);
  
  // Current session ID (this is a simplification - in a real app you'd store this in localStorage or similar)
  const [currentSessionId, setCurrentSessionId] = useState('');
  
  useEffect(() => {
    // Check if user is logged in
    if (!currentUser) {
      navigate('/marketplace/auth/signin');
      return;
    }
    
    fetchSessions();
  }, [currentUser, navigate]);
  
  const fetchSessions = async () => {
    setLoading(true);
    setError('');
    
    try {
      const userSessions = await marketplaceAuthService.getUserSessions(currentUser.uid);
      
      // Sort sessions by timestamp (newest first)
      const sortedSessions = userSessions.sort((a, b) => {
        // Handle Firestore timestamps
        const timeA = a.timestamp?.seconds ? a.timestamp.seconds * 1000 : Date.now();
        const timeB = b.timestamp?.seconds ? b.timestamp.seconds * 1000 : Date.now();
        return timeB - timeA;
      });
      
      setSessions(sortedSessions);
      
      // Set the first session as current (this is a simplification)
      if (sortedSessions.length > 0 && !currentSessionId) {
        setCurrentSessionId(sortedSessions[0].id);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
      setError('Failed to load session information');
    } finally {
      setLoading(false);
    }
  };
  
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchSessions();
    setRefreshing(false);
  };
  
  const handleTerminateSession = async (sessionId) => {
    setTerminatingSession(sessionId);
    setError('');
    
    try {
      await marketplaceAuthService.terminateSession(currentUser.uid, sessionId);
      
      // If terminating current session, sign out
      if (sessionId === currentSessionId) {
        await marketplaceAuthService.signOutUser();
        navigate('/marketplace/auth/signin');
        return;
      }
      
      // Update sessions list
      setSessions(sessions.filter(session => session.id !== sessionId));
      setSuccess('Session terminated successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error terminating session:', error);
      setError('Failed to terminate session');
    } finally {
      setTerminatingSession(null);
    }
  };
  
  const formatSessionTime = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    
    // Handle Firestore timestamps
    const date = timestamp.seconds 
      ? new Date(timestamp.seconds * 1000) 
      : new Date(timestamp);
    
    return formatDistanceToNow(date, { addSuffix: true });
  };
  
  return (
    <Container>
      <StyledPaper elevation={3}>
        <Title variant="h4">Active Sessions</Title>
        <Subtitle variant="body1">Manage your account's active login sessions</Subtitle>
        
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" color="white">
            Your Devices
          </Typography>
          <Tooltip title="Refresh sessions">
            <IconButton 
              onClick={handleRefresh} 
              disabled={refreshing}
              style={{ color: 'white' }}
            >
              {refreshing ? <CircularProgress size={24} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
        </Box>
        
        <InfoBox>
          <Typography variant="body2" color="white">
            <DeviceIcon fontSize="small" style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            These are the devices currently signed in to your account. If you don't recognize a session, 
            terminate it immediately to protect your account.
          </Typography>
        </InfoBox>
        
        {error && (
          <Alert 
            severity="error" 
            style={{ marginBottom: '1.5rem', backgroundColor: 'rgba(211, 47, 47, 0.1)', color: '#f44336' }}
            onClose={() => setError('')}
          >
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert 
            severity="success" 
            style={{ marginBottom: '1.5rem', backgroundColor: 'rgba(76, 175, 80, 0.1)', color: '#4caf50' }}
            onClose={() => setSuccess('')}
          >
            {success}
          </Alert>
        )}
        
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : sessions.length === 0 ? (
          <NoSessionsMessage>
            <DeviceIcon style={{ fontSize: 48, opacity: 0.5, marginBottom: '1rem' }} />
            <Typography variant="h6">No active sessions found</Typography>
            <Typography variant="body2">
              There are no other devices signed in to your account
            </Typography>
          </NoSessionsMessage>
        ) : (
          <List>
            {sessions.map((session) => (
              <StyledListItem 
                key={session.id} 
                className={session.id === currentSessionId ? 'current-session' : ''}
              >
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center">
                      <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
                        {session.device?.browser || 'Unknown Browser'} on {session.device?.os || 'Unknown OS'}
                      </Typography>
                      {session.id === currentSessionId && (
                        <Chip 
                          label="Current Session" 
                          size="small" 
                          color="primary" 
                          style={{ marginLeft: '1rem' }}
                          icon={<ActiveIcon />}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <>
                      <DeviceInfo>
                        {getDeviceIcon(session.device?.device || 'Desktop')}
                        <Typography variant="body2" style={{ marginLeft: '0.5rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                          {session.device?.device || 'Unknown Device'}
                        </Typography>
                      </DeviceInfo>
                      <SessionTime>
                        <TimeIcon fontSize="small" style={{ marginRight: '0.5rem' }} />
                        {formatSessionTime(session.timestamp)}
                      </SessionTime>
                    </>
                  }
                />
                <ListItemSecondaryAction>
                  <Tooltip title="Terminate Session">
                    <IconButton 
                      edge="end" 
                      onClick={() => handleTerminateSession(session.id)}
                      disabled={terminatingSession === session.id}
                      style={{ color: '#f44336' }}
                    >
                      {terminatingSession === session.id ? (
                        <CircularProgress size={24} />
                      ) : (
                        <DeleteIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </StyledListItem>
            ))}
          </List>
        )}
        
        <Divider style={{ margin: '1.5rem 0', backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
        
        <Box display="flex" justifyContent="space-between">
          <ActionButton 
            variant="outlined" 
            onClick={() => navigate('/marketplace/profile')}
            style={{ color: 'white', borderColor: 'white' }}
          >
            Back to Profile
          </ActionButton>
          
          <ActionButton 
            variant="contained" 
            color="error" 
            onClick={() => {
              // Show confirmation dialog
              if (window.confirm('Are you sure you want to sign out from all devices? This will terminate all active sessions including your current one.')) {
                // In a real app, you'd implement a function to terminate all sessions
                sessions.forEach(session => {
                  marketplaceAuthService.terminateSession(currentUser.uid, session.id);
                });
                marketplaceAuthService.signOutUser();
                navigate('/marketplace/auth/signin');
              }
            }}
            disabled={sessions.length === 0 || loading}
          >
            Sign Out All Devices
          </ActionButton>
        </Box>
      </StyledPaper>
    </Container>
  );
};

export default SessionManager;
